import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Card, Col, message, Popconfirm, Row } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	brandAssetGroupsRestCrudThunksForBuyer,
	brandAssetGroupsRestCrudThunksForSupplier,
} from '../../thunks/brand_asset_groups_thunks';
import {
	brandsRestCrudThunksForBuyer,
	brandsRestCrudThunksForSupplier,
} from '../../thunks/brands_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AddOrEditAssetGroupModal from '../brand_detail_page/AddOrEditAssetGroupModal';
import PaginatedReduxTableBase from '../paginated_redux_table/PaginatedReduxTable';
import { withUserType } from '../withUserType';
import OWUploadBase from '../OWUpload';
import { nonHeicImageUploadValidation } from '../../utils/ImageUtils';
import { getProtectedImageUriFromString } from '../../utils/FileAccessUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import OWTag from '../common/OWTag';
import MultiLineComponent from '../common/MultiLineComponent';
import { EmptyState } from '../empty_state/EmptyState';

const PaginatedReduxTable = PaginatedReduxTableBase as any;
const OWUpload = OWUploadBase as any;

const TARGET_COLLECTION_NAME = 'brandAssetGroupsIndex';

const BrandDetailDetailsPage: FC<any> = ({
	match,
	userType,
	brand,
	deleteAssetGroup,
	fetchAssetGroups,
	updateBrand,
}): React.ReactElement => {
	const brandId = match.params.id;
	const preAppliedFilters = useMemo(() => ({ brandId }), [brandId]);

	const [modalVisible, setModalVisible] = useState(false);
	const [editingAssetGroup, setEditingAssetGroup] = useState(null);
	const [deletingId, setDeletingId] = useState<number | null>(null);
	const [uploading, setUploading] = useState(false);

	const logoUrl = useMemo(
		() =>
			nullSafeGet('logoAttachment.fileId', brand)
				? getProtectedImageUriFromString(userType)(
						`${brand.logoAttachment.fileId}/${brand.logoAttachment.fileName}`
				  )
				: '',
		[brand, userType]
	);

	const refreshAssetGroups = useCallback(() => {
		fetchAssetGroups(preAppliedFilters, TARGET_COLLECTION_NAME);
	}, [fetchAssetGroups, preAppliedFilters]);

	const onAdd = useCallback(() => {
		setEditingAssetGroup(null);
		setModalVisible(true);
	}, []);

	const onEdit = useCallback((record) => {
		setEditingAssetGroup(record);
		setModalVisible(true);
	}, []);

	const onModalCancel = useCallback(() => {
		setModalVisible(false);
		setEditingAssetGroup(null);
	}, []);

	const onModalSuccess = useCallback(() => {
		setModalVisible(false);
		setEditingAssetGroup(null);
		refreshAssetGroups();
	}, [refreshAssetGroups]);

	const onDelete = useCallback(
		(record) => {
			setDeletingId(record.id);
			deleteAssetGroup(record.id)
				.then(() => {
					message.success('Asset group deleted successfully!');
					refreshAssetGroups();
				})
				.catch((err) => message.error(err))
				.finally(() => setDeletingId(null));
		},
		[deleteAssetGroup, refreshAssetGroups]
	);

	const handleLogoUploadChange = useCallback(
		(info) => {
			if (info.file.status === 'uploading') {
				setUploading(true);
				return;
			}
			if (info.file.status === 'done') {
				const fileName = nullSafeGet('file.response.data.fileName', info) || '';
				const fileId = nullSafeGet('file.response.data.fileId', info) || '';
				setUploading(false);
				updateBrand({ ...brand, logoAttachment: { fileName, fileId } })
					.then(() => message.success('Logo updated successfully!'))
					.catch((err) => message.error(err));
			}
		},
		[brand, updateBrand]
	);

	const onLogoUploadError = useCallback(() => {
		message.error('Image upload failed! Please contact support!');
		setUploading(false);
	}, []);

	const addButton = useMemo(
		() => (
			<Button type="primary" onClick={onAdd}>
				Add
			</Button>
		),
		[onAdd]
	);

	const columns = [
		{
			title: 'Group Name',
			dataIndex: 'name',
		},
		{
			title: 'Asset Details',
			dataIndex: 'assetTemplates',
			render: (ad, record) => (
				<MultiLineComponent lines={5}>
					<div className="flex flex-row flex-wrap items-center gap-2">
						{ad.map((detail, idx) => (
							<OWTag
								key={`ad-${record.name}-${idx}`}
							>{`${detail.name} - ${detail.typeName}`}</OWTag>
						))}
					</div>
				</MultiLineComponent>
			),
		},
		{
			key: 'actions',
			align: 'right',
			render: (_, record) => (
				<div className="flex flex-row justify-end">
					<Button type="link" onClick={() => onEdit(record)}>
						Edit
					</Button>
					<Popconfirm
						title="Are you sure you want to delete this asset group?"
						onConfirm={() => onDelete(record)}
						okText="Yes"
						cancelText="No"
					>
						<Button type="link" danger loading={deletingId === record.id}>
							Delete
						</Button>
					</Popconfirm>
				</div>
			),
		},
	];

	return (
		<Row gutter={16} style={{ padding: '0 0.5em' }}>
			<Col lg={24} xl={16}>
				<div className="rowSpacing">
					<Card title="Asset Group" extra={addButton}>
						<PaginatedReduxTable
							collectionName="brand_asset_groups"
							targetCollectionName={TARGET_COLLECTION_NAME}
							columns={columns}
							showHeader
							keyAccessor={(el) => el.id}
							initialFilters={preAppliedFilters}
							initialPagination={{ current: 1, pageSize: 10 }}
							initialSorting={{}}
							fetchData={
								userType === ROLE_TYPES.SUPPLIER
									? brandAssetGroupsRestCrudThunksForSupplier.read
									: brandAssetGroupsRestCrudThunksForBuyer.read
							}
							emptyState={
								<EmptyState
									headline="No asset groups yet"
									body={<div className="mt-2">{addButton}</div>}
								/>
							}
						/>
					</Card>
				</div>
			</Col>
			<Col lg={24} xl={8}>
				<div className="rowSpacing">
					<Card
						title="Logo"
						extra={
							<OWUpload
								accept="image/*"
								name="file"
								showUploadList={false}
								beforeUpload={nonHeicImageUploadValidation}
								onChange={handleLogoUploadChange}
								onError={onLogoUploadError}
							>
								<Button type="primary" loading={uploading}>
									Upload
								</Button>
							</OWUpload>
						}
					>
						<div className="flex flex-col items-center">
							{logoUrl ? (
								<img
									src={logoUrl}
									alt={nullSafeGet('brandName', brand)}
									className="max-h-40 max-w-full object-contain"
								/>
							) : (
								<div className="flex h-40 w-full items-center justify-center rounded border border-dashed border-gray-300 text-gray-400">
									No logo uploaded
								</div>
							)}
						</div>
					</Card>
				</div>
			</Col>
			{modalVisible && (
				<AddOrEditAssetGroupModal
					brandId={brandId}
					assetGroup={editingAssetGroup}
					onCancel={onModalCancel}
					onSuccess={onModalSuccess}
				/>
			)}
		</Row>
	);
};

const mapStateToProps = (state) => ({
	brand: state.brands.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	deleteAssetGroup: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandAssetGroupsRestCrudThunksForSupplier.delete({ id }, 'id')
				: brandAssetGroupsRestCrudThunksForBuyer.delete({ id }, 'id')
		),
	fetchAssetGroups: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandAssetGroupsRestCrudThunksForSupplier.read(params, targetCollectionName)
				: brandAssetGroupsRestCrudThunksForBuyer.read(params, targetCollectionName)
		),
	updateBrand: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? brandsRestCrudThunksForSupplier.update(entity)
				: brandsRestCrudThunksForBuyer.update(entity)
		),
});

export default withUserType(
	withRouter(
		connect(mapStateToProps, mapDispatchToProps)(BrandDetailDetailsPage as any) as any
	) as any
);
